import { useRef, useState } from 'react';
import type { ReactElement } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';

interface Props {
  open: boolean;
  onClose: () => void;
  onFileSelected: (file: File) => void;
}

export function FileUploadModal({ open, onClose, onFileSelected }: Props): ReactElement {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file?.type.startsWith('image/')) {
      onFileSelected(file);
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      e.target.value = '';
      onFileSelected(file);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className="vtwo:flex vtwo:items-center vtwo:justify-between">
        Upload image
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="vtwo:hidden"
          onChange={handleFileChange}
        />
        <Box
          className={`vtwo:flex vtwo:flex-col vtwo:items-center vtwo:justify-center vtwo:gap-3 vtwo:border-2 vtwo:border-dashed vtwo:rounded-xl vtwo:p-12 vtwo:cursor-pointer vtwo:transition-colors ${
            isDragging
              ? 'vtwo:border-blue-400 vtwo:bg-blue-50'
              : 'vtwo:border-gray-300 vtwo:bg-gray-50'
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <CloudUploadIcon className="vtwo:text-gray-400 vtwo:text-5xl" />
          <Typography variant="body1" className="vtwo:text-gray-600 vtwo:font-medium">
            Drag & drop an image here
          </Typography>
          <Typography variant="body2" className="vtwo:text-gray-400">
            or
          </Typography>
          <Button
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
          >
            Browse file
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
